<template>
  <div>
    <v-skeleton-loader
      v-if="isLoading"
      type="card, card, table, table"
    />

    <div
      v-else
      class="pb-15"
    >
      <!-- 戻るボタン -->
      <div class="mb-4">
        <v-btn
          color="secondary"
          :ripple="false"
          @click="goBack"
        >
          <v-icon left>
            {{ icons.mdiArrowLeft }}
          </v-icon>
          一覧に戻る
        </v-btn>
      </div>

      <!-- 顧客基本情報 -->
      <v-card class="mb-4">
        <v-card-title>顧客詳細</v-card-title>
        <v-card-text>
          <v-row>
            <v-col
              cols="12"
              sm="6"
              md="3"
            >
              <div class="text-subtitle-2 text--secondary">
                ID
              </div>
              <div>{{ customer.id }}</div>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
            >
              <div class="text-subtitle-2 text--secondary">
                名前
              </div>
              <div>{{ customer.name }}</div>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
            >
              <div class="text-subtitle-2 text--secondary">
                メモ
              </div>
              <div class="text-pre-wrap">
                {{ customer.memo || '-' }}
              </div>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
            >
              <div class="text-subtitle-2 text--secondary">
                来店回数
              </div>
              <div>{{ customer.visitCount || 0 }}回</div>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
            >
              <div class="text-subtitle-2 text--secondary">
                最終来店日時
              </div>
              <div>{{ formatDateTime(customer.lastVisitedAt) }}</div>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
            >
              <div class="text-subtitle-2 text--secondary">
                作成日時
              </div>
              <div>{{ formatDateTime(customer.createdAt) }}</div>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
            >
              <div class="text-subtitle-2 text--secondary">
                更新日時
              </div>
              <div>{{ formatDateTime(customer.updatedAt) }}</div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- 日付範囲フィルター -->
      <v-card class="mb-4">
        <v-card-text>
          <div class="text-subtitle-1 mb-2">
            購入履歴の期間指定
          </div>
          <v-row align="center">
            <v-col
              cols="12"
              sm="4"
            >
              <v-menu
                v-model="startDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                min-width="auto"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    v-model="startDate"
                    label="開始日"
                    :prepend-icon="icons.mdiCalendar"
                    readonly
                    dense
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="startDate"
                  locale="ja"
                  :day-format="date => new Date(date).getDate()"
                  color="primary"
                  no-title
                  @input="startDateMenu = false"
                />
              </v-menu>
            </v-col>
            <v-col
              cols="12"
              sm="4"
            >
              <v-menu
                v-model="endDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                min-width="auto"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    v-model="endDate"
                    label="終了日"
                    :prepend-icon="icons.mdiCalendar"
                    readonly
                    dense
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="endDate"
                  locale="ja"
                  :day-format="date => new Date(date).getDate()"
                  color="primary"
                  no-title
                  @input="endDateMenu = false"
                />
              </v-menu>
            </v-col>
            <v-col
              cols="12"
              sm="4"
            >
              <v-btn
                color="primary"
                :ripple="false"
                @click="fetchCustomerStats"
              >
                絞り込み
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- 購買履歴サマリー -->
      <v-card
        v-if="customer.summary"
        class="mb-4"
      >
        <v-card-title>購入履歴サマリー（{{ startDate }} 〜 {{ endDate }}）</v-card-title>
        <v-card-text>
          <v-row>
            <v-col
              cols="6"
              sm="3"
            >
              <v-card
                color="blue lighten-5"
                flat
              >
                <v-card-text class="text-center">
                  <div class="text-subtitle-2 text--secondary">
                    総購入金額（大計）
                  </div>
                  <div class="text-h6 blue--text text--darken-2">
                    {{ formatCurrency(customer.summary.totalPurchaseAmountWithTax) }}
                  </div>
                  <div class="text-caption text--secondary mt-1">
                    小計: {{ formatCurrency(customer.summary.totalPurchaseAmount) }}
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col
              cols="6"
              sm="3"
            >
              <v-card
                color="green lighten-5"
                flat
              >
                <v-card-text class="text-center">
                  <div class="text-subtitle-2 text--secondary">
                    来店回数
                  </div>
                  <div class="text-h6 green--text text--darken-2">
                    {{ customer.summary.visitCount }}回
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col
              cols="6"
              sm="3"
            >
              <v-card
                color="purple lighten-5"
                flat
              >
                <v-card-text class="text-center">
                  <div class="text-subtitle-2 text--secondary">
                    平均購入金額
                  </div>
                  <div class="text-h6 purple--text text--darken-2">
                    {{ formatCurrency(customer.summary.averagePurchaseAmount) }}
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col
              cols="6"
              sm="3"
            >
              <v-card
                color="orange lighten-5"
                flat
              >
                <v-card-text class="text-center">
                  <div class="text-subtitle-2 text--secondary">
                    最終来店日
                  </div>
                  <div class="text-h6 orange--text text--darken-2">
                    {{ formatDate(customer.summary.lastVisitedAt) }}
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- タグ別売上（権限がある場合のみ表示） -->
      <v-card
        v-if="canViewCustomerStats && customer.salesByVendibleTag && customer.salesByVendibleTag.length > 0"
        class="mb-4"
      >
        <v-card-title>タグ別売上</v-card-title>
        <v-card-text>
          <v-simple-table>
            <template #default>
              <thead>
                <tr>
                  <th>タグ</th>
                  <th class="text-right">
                    金額（大計/小計）
                  </th>
                  <th class="text-right">
                    数量
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(tag, index) in customer.salesByVendibleTag"
                  :key="index"
                >
                  <td>{{ tag.vendibleTagName || '未分類' }}</td>
                  <td class="text-right">
                    <div>{{ formatCurrency(tag.totalAmountWithTax) }}</div>
                    <div class="text-caption text--secondary">
                      {{ formatCurrency(tag.totalAmount) }}
                    </div>
                  </td>
                  <td class="text-right">
                    {{ tag.quantity }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>

      <!-- 受領者別売上（権限がある場合のみ表示） -->
      <v-card
        v-if="canViewCustomerStats && customer.salesByRecipient && customer.salesByRecipient.length > 0"
        class="mb-4"
      >
        <v-card-title>受領者別売上</v-card-title>
        <v-card-text>
          <div class="text-caption mb-2">
            <v-chip
              x-small
              color="blue lighten-4"
              class="mr-2"
            >
              確定
            </v-chip>顧客に直接紐づいた販売履歴
            <v-chip
              x-small
              color="yellow lighten-3"
              class="ml-4 mr-2"
            >
              推定
            </v-chip>テーブル経由（顧客数で均等割）
            <span class="ml-4">※金額は大計/小計</span>
          </div>
          <v-simple-table>
            <template #default>
              <thead>
                <tr>
                  <th>受領者</th>
                  <th class="text-right blue--text">
                    確定
                  </th>
                  <th class="text-right orange--text">
                    推定
                  </th>
                  <th class="text-right grey lighten-3 font-weight-bold">
                    合計
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(recipient, index) in customer.salesByRecipient"
                  :key="index"
                >
                  <td>{{ recipient.recipientName || '未指定' }}</td>
                  <td class="text-right blue--text">
                    <div>{{ formatCurrency(recipient.confirmedAmountWithTax) }}</div>
                    <div class="text-caption text--secondary">
                      {{ formatCurrency(recipient.confirmedAmount) }}
                    </div>
                  </td>
                  <td class="text-right orange--text">
                    <div>{{ formatCurrency(recipient.estimatedAmountWithTax) }}</div>
                    <div class="text-caption text--secondary">
                      {{ formatCurrency(recipient.estimatedAmount) }}
                    </div>
                  </td>
                  <td class="text-right grey lighten-4 font-weight-bold">
                    <div>{{ formatCurrency(recipient.totalAmountWithTax) }}</div>
                    <div class="text-caption text--secondary">
                      {{ formatCurrency(recipient.totalAmount) }}
                    </div>
                  </td>
                </tr>
              </tbody>
              <tfoot class="grey lighten-3">
                <tr>
                  <td class="font-weight-bold">
                    合計
                  </td>
                  <td class="text-right font-weight-bold blue--text">
                    <div>{{ formatCurrency(recipientConfirmedTotalWithTax) }}</div>
                    <div class="text-caption text--secondary">
                      {{ formatCurrency(recipientConfirmedTotal) }}
                    </div>
                  </td>
                  <td class="text-right font-weight-bold orange--text">
                    <div>{{ formatCurrency(recipientEstimatedTotalWithTax) }}</div>
                    <div class="text-caption text--secondary">
                      {{ formatCurrency(recipientEstimatedTotal) }}
                    </div>
                  </td>
                  <td class="text-right font-weight-bold">
                    <div>{{ formatCurrency(recipientGrandTotalWithTax) }}</div>
                    <div class="text-caption text--secondary">
                      {{ formatCurrency(recipientGrandTotal) }}
                    </div>
                  </td>
                </tr>
              </tfoot>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>

      <!-- クロス集計マトリックス（受領者×タグ）（権限がある場合のみ表示） -->
      <v-card
        v-if="canViewCustomerStats && customer.salesMatrix && customer.salesMatrix.vendibleTags && customer.salesMatrix.vendibleTags.length > 0"
        class="mb-4"
      >
        <v-card-title>クロス集計（受領者×タグ）</v-card-title>
        <v-card-text>
          <div class="text-caption mb-2">
            各セル: <span class="blue--text">確定</span> / <span class="orange--text">推定</span> / <span class="font-weight-bold">合計</span>（大計/小計）
          </div>
          <div style="overflow-x: auto;">
            <v-simple-table dense>
              <template #default>
                <thead>
                  <tr>
                    <th class="sticky-left">
                      受領者
                    </th>
                    <th
                      v-for="tag in customer.salesMatrix.vendibleTags"
                      :key="tag"
                      class="text-center"
                    >
                      {{ tag || '未分類' }}
                    </th>
                    <th class="text-center grey lighten-3">
                      合計
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(recipient, index) in customer.salesMatrix.recipients"
                    :key="index"
                  >
                    <td class="sticky-left font-weight-medium">
                      {{ recipient.recipientName || '未指定' }}
                    </td>
                    <td
                      v-for="tag in customer.salesMatrix.vendibleTags"
                      :key="tag"
                      class="text-right"
                    >
                      <div class="d-flex flex-column align-end">
                        <span class="blue--text text-caption">
                          {{ formatCurrency(getMatrixCellValue(recipient, tag, 'confirmed_with_tax')) }}
                          <span class="text--secondary">/{{ formatCurrency(getMatrixCellValue(recipient, tag, 'confirmed')) }}</span>
                        </span>
                        <span class="orange--text text-caption">
                          {{ formatCurrency(getMatrixCellValue(recipient, tag, 'estimated_with_tax')) }}
                          <span class="text--secondary">/{{ formatCurrency(getMatrixCellValue(recipient, tag, 'estimated')) }}</span>
                        </span>
                        <span class="font-weight-bold text-caption border-top">
                          {{ formatCurrency(getMatrixCellValue(recipient, tag, 'total_with_tax')) }}
                          <span class="text--secondary">/{{ formatCurrency(getMatrixCellValue(recipient, tag, 'total')) }}</span>
                        </span>
                      </div>
                    </td>
                    <td class="text-right grey lighten-4">
                      <div class="d-flex flex-column align-end">
                        <span class="blue--text text-caption">
                          {{ formatCurrency(recipient.confirmedTotalWithTax) }}
                          <span class="text--secondary">/{{ formatCurrency(recipient.confirmedTotal) }}</span>
                        </span>
                        <span class="orange--text text-caption">
                          {{ formatCurrency(recipient.estimatedTotalWithTax) }}
                          <span class="text--secondary">/{{ formatCurrency(recipient.estimatedTotal) }}</span>
                        </span>
                        <span class="font-weight-bold text-caption border-top">
                          {{ formatCurrency(recipient.totalWithTax) }}
                          <span class="text--secondary">/{{ formatCurrency(recipient.total) }}</span>
                        </span>
                      </div>
                    </td>
                  </tr>
                </tbody>
                <tfoot class="grey lighten-3">
                  <tr>
                    <td class="sticky-left font-weight-bold">
                      合計
                    </td>
                    <td
                      v-for="tag in customer.salesMatrix.vendibleTags"
                      :key="tag"
                      class="text-right"
                    >
                      <div class="d-flex flex-column align-end">
                        <span class="blue--text text-caption font-weight-bold">
                          {{ formatCurrency(getTagTotal(tag, 'confirmed_with_tax')) }}
                          <span class="text--secondary">/{{ formatCurrency(getTagTotal(tag, 'confirmed')) }}</span>
                        </span>
                        <span class="orange--text text-caption font-weight-bold">
                          {{ formatCurrency(getTagTotal(tag, 'estimated_with_tax')) }}
                          <span class="text--secondary">/{{ formatCurrency(getTagTotal(tag, 'estimated')) }}</span>
                        </span>
                        <span class="font-weight-bold text-caption border-top">
                          {{ formatCurrency(getTagTotal(tag, 'total_with_tax')) }}
                          <span class="text--secondary">/{{ formatCurrency(getTagTotal(tag, 'total')) }}</span>
                        </span>
                      </div>
                    </td>
                    <td class="text-right">
                      <div class="d-flex flex-column align-end">
                        <span class="blue--text text-caption font-weight-bold">
                          {{ formatCurrency(matrixConfirmedTotalWithTax) }}
                          <span class="text--secondary">/{{ formatCurrency(matrixConfirmedTotal) }}</span>
                        </span>
                        <span class="orange--text text-caption font-weight-bold">
                          {{ formatCurrency(matrixEstimatedTotalWithTax) }}
                          <span class="text--secondary">/{{ formatCurrency(matrixEstimatedTotal) }}</span>
                        </span>
                        <span class="font-weight-bold text-caption border-top">
                          {{ formatCurrency(matrixGrandTotalWithTax) }}
                          <span class="text--secondary">/{{ formatCurrency(matrixGrandTotal) }}</span>
                        </span>
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </template>
            </v-simple-table>
          </div>
        </v-card-text>
      </v-card>

      <!-- 来店履歴 -->
      <v-card>
        <v-card-title>
          来店履歴
          <v-spacer />
          <v-chip
            small
            color="primary"
            :outlined="!$vuetify.theme.dark"
          >
            総来店回数: <span class="font-weight-bold ml-1">{{ customer.visitCount || 0 }}</span>回
          </v-chip>
        </v-card-title>
        <v-card-text>
          <v-data-table
            :options="visitHistoryOptions"
            :headers="visitHistoryHeaders"
            :items="visitHistory"
            :loading="isVisitHistoryLoading"
            :server-items-length="visitHistoryTotalCount"
            :footer-props="{
              itemsPerPageText: '行/ページ:',
              itemsPerPageOptions: [10, 25, 50],
            }"
            no-data-text="来店履歴がありません"
            loading-text="読み込み中..."
            @update:options="handleVisitHistoryOptionsUpdate"
          >
            <template #[`item.visitedAt`]="{ item }">
              {{ formatDateTime(item.visitedAt) }}
            </template>

            <template #[`item.customerCount`]="{ item }">
              {{ item.customerCount || 0 }}名
            </template>

            <template #[`item.paidAmount`]="{ item }">
              {{ formatCurrency(item.paidAmount) }}
            </template>

            <template #[`item.total`]="{ item }">
              {{ formatCurrency(item.total) }}
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
import {
  ref,
  reactive,
  computed,
  getCurrentInstance,
} from '@vue/composition-api'
import { mdiCalendar, mdiArrowLeft } from '@mdi/js'
import { format, startOfMonth, parseISO } from 'date-fns'
import CustomerApi from '@/api/waiter/Customer'
import useAbilityCheck from '@/views/composable/useAbilityCheck'

export default {
  setup() {
    const vm = getCurrentInstance().proxy
    const { canViewCustomerStats } = useAbilityCheck()

    // ローディング状態
    const isLoading = ref(false)
    const isVisitHistoryLoading = ref(false)

    // 来店履歴ページネーション
    const visitHistory = ref([])
    const visitHistoryTotalCount = ref(0)
    const visitHistoryOptions = ref({
      page: 1,
      itemsPerPage: 10,
    })

    const visitHistoryHeaders = [
      { text: '日時', value: 'visitedAt', sortable: false },
      {
        text: '人数', value: 'customerCount', sortable: false, align: 'center',
      },
      {
        text: '支払い額', value: 'paidAmount', sortable: false, align: 'end',
      },
      {
        text: '合計額', value: 'total', sortable: false, align: 'end',
      },
    ]

    // 日付フィルター
    const startDateMenu = ref(false)
    const endDateMenu = ref(false)
    const startDate = ref(format(startOfMonth(new Date()), 'yyyy-MM-dd'))
    const endDate = ref(format(new Date(), 'yyyy-MM-dd'))

    // 顧客データ
    const customer = reactive({
      id: null,
      name: '',
      memo: '',
      visitCount: 0,
      visitHistory: [],
      lastVisitedAt: null,
      createdAt: null,
      updatedAt: null,
      summary: null,
      salesByVendibleTag: [],
      salesByRecipient: [],
      salesMatrix: null,
    })

    // 受領者別売上の合計（確定・小計）
    const recipientConfirmedTotal = computed(() => {
      if (!customer.salesByRecipient) return 0

      return customer.salesByRecipient.reduce((sum, r) => sum + (r.confirmedAmount || 0), 0)
    })

    // 受領者別売上の合計（確定・大計）
    const recipientConfirmedTotalWithTax = computed(() => {
      if (!customer.salesByRecipient) return 0

      return customer.salesByRecipient.reduce((sum, r) => sum + (r.confirmedAmountWithTax || 0), 0)
    })

    // 受領者別売上の合計（推定・小計）
    const recipientEstimatedTotal = computed(() => {
      if (!customer.salesByRecipient) return 0

      return customer.salesByRecipient.reduce((sum, r) => sum + (r.estimatedAmount || 0), 0)
    })

    // 受領者別売上の合計（推定・大計）
    const recipientEstimatedTotalWithTax = computed(() => {
      if (!customer.salesByRecipient) return 0

      return customer.salesByRecipient.reduce((sum, r) => sum + (r.estimatedAmountWithTax || 0), 0)
    })

    // 受領者別売上の合計（総計・小計）
    const recipientGrandTotal = computed(() => {
      if (!customer.salesByRecipient) return 0

      return customer.salesByRecipient.reduce((sum, r) => sum + (r.totalAmount || 0), 0)
    })

    // 受領者別売上の合計（総計・大計）
    const recipientGrandTotalWithTax = computed(() => {
      if (!customer.salesByRecipient) return 0

      return customer.salesByRecipient.reduce((sum, r) => sum + (r.totalAmountWithTax || 0), 0)
    })

    // マトリクスの確定合計（小計）
    const matrixConfirmedTotal = computed(() => {
      if (!customer.salesMatrix || !customer.salesMatrix.recipients) return 0

      return customer.salesMatrix.recipients.reduce((sum, r) => sum + (r.confirmedTotal || 0), 0)
    })

    // マトリクスの確定合計（大計）
    const matrixConfirmedTotalWithTax = computed(() => {
      if (!customer.salesMatrix || !customer.salesMatrix.recipients) return 0

      return customer.salesMatrix.recipients.reduce((sum, r) => sum + (r.confirmedTotalWithTax || 0), 0)
    })

    // マトリクスの推定合計（小計）
    const matrixEstimatedTotal = computed(() => {
      if (!customer.salesMatrix || !customer.salesMatrix.recipients) return 0

      return customer.salesMatrix.recipients.reduce((sum, r) => sum + (r.estimatedTotal || 0), 0)
    })

    // マトリクスの推定合計（大計）
    const matrixEstimatedTotalWithTax = computed(() => {
      if (!customer.salesMatrix || !customer.salesMatrix.recipients) return 0

      return customer.salesMatrix.recipients.reduce((sum, r) => sum + (r.estimatedTotalWithTax || 0), 0)
    })

    // マトリクスの総計（小計）
    const matrixGrandTotal = computed(() => {
      if (!customer.salesMatrix || !customer.salesMatrix.recipients) return 0

      return customer.salesMatrix.recipients.reduce((sum, r) => sum + (r.total || 0), 0)
    })

    // マトリクスの総計（大計）
    const matrixGrandTotalWithTax = computed(() => {
      if (!customer.salesMatrix || !customer.salesMatrix.recipients) return 0

      return customer.salesMatrix.recipients.reduce((sum, r) => sum + (r.totalWithTax || 0), 0)
    })

    // マトリクスのセル値を取得
    const getMatrixCellValue = (recipient, tag, type) => {
      if (!recipient || !recipient.byTag || !recipient.byTag[tag]) return 0
      const cell = recipient.byTag[tag]
      if (typeof cell === 'object') {
        return cell[type] || 0
      }

      // 古い形式（数値のみ）の場合
      return type === 'total' ? (cell || 0) : 0
    }

    // タグ別合計を取得
    const getTagTotal = (tag, type) => {
      if (!customer.salesMatrix || !customer.salesMatrix.totalsByTag) return 0
      const totals = customer.salesMatrix.totalsByTag
      if (totals[type] && totals[type][tag] !== undefined) {
        return totals[type][tag]
      }

      // 古い形式の場合
      return type === 'total' ? (totals[tag] || 0) : 0
    }

    // フォーマット関数
    const formatCurrency = value => {
      if (value == null) return '¥0'

      return new Intl.NumberFormat('ja-JP', {
        style: 'currency',
        currency: 'JPY',
        maximumFractionDigits: 0,
      }).format(value)
    }

    const formatDate = value => {
      if (!value) return '-'

      return format(parseISO(value), 'yyyy/MM/dd')
    }

    const formatDateTime = value => {
      if (!value) return '-'

      return format(parseISO(value), 'yyyy/MM/dd HH:mm')
    }

    // API呼び出し
    const fetchCustomerStats = async () => {
      const { customerId } = vm.$route.params
      if (!customerId) return

      isLoading.value = true

      try {
        const res = await CustomerApi.getCustomerStats(customerId, {
          startDate: startDate.value,
          endDate: endDate.value,
        })

        if (res?.data) {
          Object.assign(customer, res.data)
        }
      } catch (error) {
        vm.$toast.error('顧客データの取得に失敗しました')
      } finally {
        isLoading.value = false
      }
    }

    // 来店履歴の取得（ページネーション付き）
    const fetchVisitHistory = async () => {
      const { customerId } = vm.$route.params
      if (!customerId) return

      isVisitHistoryLoading.value = true

      try {
        const res = await CustomerApi.getCustomerStatsVisitHistory(customerId, {
          page: visitHistoryOptions.value.page,
          perPage: visitHistoryOptions.value.itemsPerPage,
        })

        if (res?.data) {
          visitHistory.value = res.data.visitHistory || []
          visitHistoryTotalCount.value = res.data.pagination?.totalCount || 0
        }
      } catch (error) {
        vm.$toast.error('来店履歴の取得に失敗しました')
      } finally {
        isVisitHistoryLoading.value = false
      }
    }

    // 来店履歴テーブルのオプション変更ハンドラ
    const handleVisitHistoryOptionsUpdate = options => {
      visitHistoryOptions.value = {
        page: options.page,
        itemsPerPage: options.itemsPerPage,
      }
      fetchVisitHistory()
    }

    const goBack = () => {
      vm.$router.push({ name: 'admin-customer-stats' })
    }

    // 初期データ取得
    fetchCustomerStats()

    return {
      // data
      isLoading,
      isVisitHistoryLoading,
      startDateMenu,
      endDateMenu,
      startDate,
      endDate,
      customer,
      visitHistory,
      visitHistoryOptions,
      visitHistoryHeaders,
      visitHistoryTotalCount,
      recipientConfirmedTotal,
      recipientConfirmedTotalWithTax,
      recipientEstimatedTotal,
      recipientEstimatedTotalWithTax,
      recipientGrandTotal,
      recipientGrandTotalWithTax,
      matrixConfirmedTotal,
      matrixConfirmedTotalWithTax,
      matrixEstimatedTotal,
      matrixEstimatedTotalWithTax,
      matrixGrandTotal,
      matrixGrandTotalWithTax,
      canViewCustomerStats,

      // methods
      formatCurrency,
      formatDate,
      formatDateTime,
      fetchCustomerStats,
      fetchVisitHistory,
      handleVisitHistoryOptionsUpdate,
      getMatrixCellValue,
      getTagTotal,
      goBack,

      icons: {
        mdiCalendar,
        mdiArrowLeft,
      },
    }
  },
}
</script>

<style scoped>
.sticky-left {
  position: sticky;
  left: 0;
  background-color: inherit;
  z-index: 1;
}

.text-pre-wrap {
  white-space: pre-wrap;
}

.border-top {
  border-top: 1px solid #e0e0e0;
  padding-top: 2px;
  margin-top: 2px;
}
</style>
